import { importProvidersFrom } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFirestoreModule } from '@angular/fire/compat/firestore';
import { AngularFireAuthModule } from '@angular/fire/compat/auth';
import { AngularFireFunctionsModule } from '@angular/fire/compat/functions';
import { AngularFireStorageModule } from '@angular/fire/compat/storage';
import { MatNativeDateModule } from '@angular/material/core';
import { NgxStripeModule } from 'ngx-stripe';
import { initializeApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaEnterpriseProvider, CustomProvider } from 'firebase/app-check';
import { AppComponent } from './app/app.component';
import { APP_ROUTES } from './app/app.routes';
import { environment } from './environments/environment';

// ── Step 1: debug token must be set BEFORE initializeAppCheck ─────────────────
if (!environment.production &&
    environment.appCheckDebugToken &&
    environment.appCheckDebugToken !== 'REPLACE_WITH_DEBUG_TOKEN') {
  (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN = environment.appCheckDebugToken;
}

// ── Step 2: initialize Firebase synchronously before bootstrap ────────────────
// AngularFireModule.initializeApp() will detect the existing [DEFAULT] app and
// reuse it — no duplicate-app error.
const firebaseApp = initializeApp(environment.firebaseConfig);

// ── Step 3: initialize App Check immediately after Firebase ───────────────────
// Must happen before any Firestore/Storage/Functions request.
// Spokes use reCAPTCHA Enterprise (register-app.js calls recaptchaEnterpriseConfig API).
const isLocalhost = typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
const useEmulators = (environment as any).useEmulators === true;

if (!useEmulators) {
  initializeAppCheck(firebaseApp, {
    provider: !isLocalhost && environment.recaptchaSiteKey
      ? new ReCaptchaEnterpriseProvider(environment.recaptchaSiteKey)
      : new CustomProvider({
          getToken: () => Promise.reject(new Error('Dev: App Check debug token active')),
        }),
    isTokenAutoRefreshEnabled: true,
  });
}

if (useEmulators) {
  import('firebase/auth').then(({ getAuth, connectAuthEmulator }) => {
    connectAuthEmulator(getAuth(firebaseApp), 'http://localhost:9099', { disableWarnings: true });
  });
  import('firebase/firestore').then(({ getFirestore, connectFirestoreEmulator }) => {
    connectFirestoreEmulator(getFirestore(firebaseApp), 'localhost', 8080);
  });
  import('firebase/functions').then(({ getFunctions, connectFunctionsEmulator }) => {
    connectFunctionsEmulator(getFunctions(firebaseApp), 'localhost', 5001);
  });
  import('firebase/storage').then(({ getStorage, connectStorageEmulator }) => {
    connectStorageEmulator(getStorage(firebaseApp), 'localhost', 9199);
  });
  console.log('🔧 Firebase Emulators connected');
}

// ── Step 4: bootstrap ─────────────────────────────────────────────────────────
bootstrapApplication(AppComponent, {
  providers: [
    provideRouter(APP_ROUTES),
    provideAnimations(),
    importProvidersFrom(
      AngularFireModule.initializeApp(environment.firebaseConfig),
      AngularFirestoreModule,
      AngularFireAuthModule,
      AngularFireFunctionsModule,
      AngularFireStorageModule,
      MatNativeDateModule,
      NgxStripeModule.forRoot(environment.stripeKey),
    ),
  ],
}).catch(err => console.error(err));
